import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useMessageStore } from '../stores/messageStore';
import { sanitizeMessage } from '../utils/contactFilter';

interface StartConversationProps {
  listingId: string;
  listingTitle: string;
  receiverId: string;
  recipientName: string;
  onMessageSent: () => void;
}

const StartConversation: React.FC<StartConversationProps> = ({
  listingId,
  listingTitle,
  receiverId,
  recipientName,
  onMessageSent
}) => {
  const [message, setMessage] = useState('');
  const { user } = useAuth();
  const { createConversation, addMessage } = useMessageStore();
  const navigate = useNavigate();

  const messageTemplates = [
    "Hi, I'm interested in your listing. Is it still available?",
    "I'd like to schedule a viewing. When would be a good time?",
    "Could you provide more details about the utilities and amenities?",
    "What's the earliest move-in date possible?"
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !message.trim()) return;

    // Check for contact information
    const { isValid, sanitizedText } = sanitizeMessage(message);
    if (!isValid) {
      alert("Direct contact information is not allowed. Please use the platform's messaging system.");
      setMessage(sanitizedText);
      return;
    }

    // Create a new conversation
    const conversationId = createConversation({
      participants: [user.id, receiverId],
      recipientId: receiverId,
      recipientName,
      listingId,
      listingTitle
    });

    // Add the first message
    addMessage(conversationId, {
      senderId: user.id,
      content: message.trim(),
      isRead: false
    });

    // Navigate to messages with the new conversation
    navigate(`/messages?conversation=${conversationId}`);
    onMessageSent();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-lg w-full">
        <div className="flex justify-between items-center p-6 border-b">
          <div>
            <h3 className="text-lg font-semibold">Contact Owner</h3>
            <p className="text-sm text-gray-600">
              Regarding: {listingTitle}
            </p>
          </div>
          <button
            onClick={onMessageSent}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6">
          {/* Quick Templates */}
          <div className="mb-6">
            <p className="text-sm text-gray-600 mb-2">Quick messages:</p>
            <div className="flex flex-wrap gap-2">
              {messageTemplates.map((template, index) => (
                <button
                  key={index}
                  onClick={() => setMessage(template)}
                  className="text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded-full transition-colors"
                >
                  {template}
                </button>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                Your message
              </label>
              <textarea
                id="message"
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                placeholder="Type your message here..."
                required
              />
            </div>

            <div className="flex items-center justify-between pt-4">
              <p className="text-sm text-gray-500">
                Be clear and professional in your communication
              </p>
              <button
                type="submit"
                disabled={!message.trim()}
                className="bg-indigo-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-indigo-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>Send</span>
                <Send className="h-4 w-4" />
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default StartConversation;